// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — App Shell (Sidebar + Main Layout)
// ─────────────────────────────────────────────────────────────────────────────
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store';
import { Badge } from '@/components/ui/index';
import {
  LayoutDashboard, FolderOpen, GitCompare, BookOpen,
  ChevronRight, Menu, X, Settings, HelpCircle,
  Building2, ChevronLeft,
} from 'lucide-react';
import { useProjects } from '@/hooks/useQueries';
import { Notifications } from './Notifications';

// ── Nav items ─────────────────────────────────────────────────────────────────

const NAV_ITEMS: Array<{ to: string; label: string; icon: React.ElementType; end?: boolean }> = [
  { to: '/',        label: 'Dashboard',   icon: LayoutDashboard,  end: true },
  { to: '/compare', label: 'Compare',     icon: GitCompare },
  { to: '/guide',   label: 'Lab Guide',   icon: BookOpen },
];

// ── Sidebar ───────────────────────────────────────────────────────────────────

function Sidebar() {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar, activeProjectId } = useAppStore();
  const { data: projects } = useProjects();

  return (
    <aside className={cn(
      'fixed top-0 left-0 h-screen z-40 flex flex-col transition-all duration-300',
      'bg-navy-900 border-r border-navy-800',
      sidebarOpen ? 'w-64' : 'w-16'
    )}>
      {/* Brand */}
      <div className="flex items-center gap-3 h-16 px-4 border-b border-navy-800">
        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center text-white font-display font-bold text-sm">
          LC
        </div>
        {sidebarOpen && (
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-white text-base truncate">LCCA Nexus</div>
            <div className="text-navy-400 text-xs truncate">Facility Management Lab</div>
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="ml-auto flex-shrink-0 p-1 rounded-lg text-navy-400 hover:text-white hover:bg-navy-700 transition-colors"
        >
          {sidebarOpen ? <ChevronLeft size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
        {sidebarOpen && (
          <p className="px-2 pt-3 pb-1 text-navy-500 text-[10px] font-display font-semibold uppercase tracking-widest">
            Navigation
          </p>
        )}

        {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => {
          const active = end ? location.pathname === to : location.pathname.startsWith(to);
          return (
            <Link key={to} to={to} className={cn('nav-item', active && 'active')}>
              <Icon size={18} className="flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{label}</span>}
            </Link>
          );
        })}

        {/* Projects list */}
        {sidebarOpen && (
          <>
            <p className="px-2 pt-5 pb-1 text-navy-500 text-[10px] font-display font-semibold uppercase tracking-widest flex items-center justify-between">
              Recent Projects
              {projects && <Badge variant="navy">{projects.length}</Badge>}
            </p>
            <div className="flex flex-col gap-0.5 max-h-56 overflow-y-auto">
              {projects?.slice(0, 8).map(p => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className={cn(
                    'nav-item text-xs py-2',
                    activeProjectId === p.id && 'active'
                  )}
                >
                  <Building2 size={14} className="flex-shrink-0" />
                  <span className="truncate">{p.name}</span>
                  <ChevronRight size={12} className="ml-auto flex-shrink-0 opacity-40" />
                </Link>
              ))}
              {!projects?.length && (
                <p className="text-xs text-navy-600 px-2 py-1">No projects yet</p>
              )}
            </div>
          </>
        )}
      </nav>

      {/* Bottom links */}
      <div className="p-2 border-t border-navy-800 flex flex-col gap-1">
        <Link to="/guide" className="nav-item">
          <HelpCircle size={18} className="flex-shrink-0" />
          {sidebarOpen && <span>Help & Guide</span>}
        </Link>
      </div>
    </aside>
  );
}

// ── Top Bar ───────────────────────────────────────────────────────────────────

function TopBar() {
  const location = useLocation();
  const { sidebarOpen } = useAppStore();

  const getTitle = () => {
    if (location.pathname === '/')               return 'Dashboard';
    if (location.pathname.startsWith('/projects')) return 'Project Analysis';
    if (location.pathname === '/compare')        return 'Scenario Comparison';
    if (location.pathname === '/guide')          return 'Lab Guide';
    return 'LCCA Nexus';
  };

  return (
    <header className={cn(
      'fixed top-0 right-0 h-16 z-30 flex items-center px-6 gap-4',
      'bg-white border-b border-parchment-200 shadow-sm transition-all duration-300',
      sidebarOpen ? 'left-64' : 'left-16'
    )}>
      <div>
        <h1 className="font-display font-bold text-navy-900 text-lg leading-tight">{getTitle()}</h1>
        <p className="text-xs text-navy-400">Smart Technologies for Facility Management</p>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <Badge variant="amber">Lab Tool</Badge>
        <div className="w-8 h-8 rounded-full bg-navy-100 flex items-center justify-center text-navy-600 font-semibold text-xs">
          ST
        </div>
      </div>
    </header>
  );
}

// ── App Shell ─────────────────────────────────────────────────────────────────

export function AppShell({ children }: { children: React.ReactNode }) {
  const { sidebarOpen } = useAppStore();

  return (
    <div className="min-h-screen bg-parchment-100">
      <Sidebar />
      <TopBar />

      <main className={cn(
        'transition-all duration-300 pt-16 min-h-screen',
        sidebarOpen ? 'pl-64' : 'pl-16'
      )}>
        <div className="p-6 max-w-screen-2xl mx-auto">
          {children}
        </div>
      </main>

      <Notifications />
    </div>
  );
}
